import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toast } from "@/lib/safe-toast";
import { Link } from "@tanstack/react-router";
import { authenticatedRoute } from "../_authenticated";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Button } from "@/components/ui";
import { toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";
import {
  invitesPanel,
  useAcceptTeamInvite,
  useCreateTeam,
  useInviteTeamMember,
  useMyTeamInvites,
  useRemoveTeamMember,
  useTeam,
} from "@/hooks/useTeam";

function TeamPage() {
  const { t, i18n } = useTranslation("common");
  const { data, isLoading, error } = useTeam();
  const myInvites = useMyTeamInvites();
  const createTeam = useCreateTeam();
  const inviteMember = useInviteTeamMember();
  const removeMember = useRemoveTeamMember();
  const acceptInvite = useAcceptTeamInvite();

  const [teamName, setTeamName] = useState("");
  const [email, setEmail] = useState("");
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const team = data?.team ?? null;
  const teamRequired = data?.teamRequired ?? false;
  // Deliberately not gated on `teamRequired`: every invitee is by definition not yet on a team
  // plan, so that flag hid the invitation from exactly the people it was addressed to. See
  // `invitesPanel` in hooks/useTeam.ts.
  const invites = invitesPanel({
    invites: myInvites.data,
    invitesError: myInvites.error,
    team,
    teamLoading: isLoading,
  });
  const canManageMembers = team?.currentUserRole === "admin";
  const locale = i18n.language || "en";

  const formatDate = (date: Date) =>
    new Date(date).toLocaleDateString(toDisplayString(locale), { dateStyle: "medium" });

  const onCreateTeam = async () => {
    if (!teamName.trim()) return;
    try {
      await createTeam.mutateAsync(teamName.trim());
      setTeamName("");
      toast.success(t("team.createSuccess"));
    } catch (e) {
      toast.error(toErrorDisplayMessage(e) || t("team.createError"));
    }
  };

  const onInvite = async () => {
    if (!email.trim()) return;
    try {
      await inviteMember.mutateAsync(email.trim());
      setEmail("");
      toast.success(t("team.inviteSuccess"));
    } catch (e) {
      toast.error(toErrorDisplayMessage(e) || t("team.inviteError"));
    }
  };

  const onRemove = async (userId: string) => {
    setRemovingUserId(userId);
    try {
      await removeMember.mutateAsync(userId);
      toast.success(t("team.removeSuccess"));
    } catch (e) {
      toast.error(toErrorDisplayMessage(e) || t("team.removeError"));
    } finally {
      setRemovingUserId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("team.title"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("team.subtitle"))}</p>
      </div>

      {isLoading && <p className="text-sm text-text-muted">{toSafeReactNode(t("team.loading"))}</p>}
      {error && <p className="text-sm text-danger">{toErrorDisplayMessage(error)}</p>}
      {!isLoading && !error && teamRequired && (
        <Card>
          <CardHeader>
            <CardTitle>{toSafeReactNode(t("team.teamSharingTitle"))}</CardTitle>
            <CardDescription>{toSafeReactNode(t("team.teamSharingDescription"))}</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/billing">
              <Button variant="primary">{toSafeReactNode(t("team.upgradeNow"))}</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {invites.kind === "unavailable" && (
        <p className="text-sm text-danger">{toSafeReactNode(t("team.invitationsUnavailable"))}</p>
      )}

      {invites.kind === "list" && (
        <Card>
          <CardHeader>
            <CardTitle>{toSafeReactNode(t("team.invitationsTitle"))}</CardTitle>
            <CardDescription>{toSafeReactNode(t("team.invitationsDescription"))}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {invites.invites.map((inv) => (
                <li
                  key={toDisplayString(inv.id)}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-2 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-text">{toSafeReactNode(inv.teamName)}</p>
                    <p className="text-xs text-text-muted">
                      {toSafeReactNode(t("team.invitedOn", { date: formatDate(new Date(inv.createdAt)) }))}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    disabled={acceptInvite.isPending}
                    onClick={async () => {
                      try {
                        await acceptInvite.mutateAsync(inv.id);
                        toast.success(t("team.acceptSuccess"));
                      } catch (e) {
                        toast.error(toErrorDisplayMessage(e) || t("team.acceptError"));
                      }
                    }}
                  >
                    {acceptInvite.isPending ? "…" : toSafeReactNode(t("team.accept"))}
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && !teamRequired && !team && (
        <Card>
          <CardHeader>
            <CardTitle>{toSafeReactNode(t("team.createTeamTitle"))}</CardTitle>
            <CardDescription>{toSafeReactNode(t("team.createTeamDescription"))}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Input
                type="text"
                placeholder={toDisplayString(t("team.teamNamePlaceholder"))}
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="max-w-xs"
              />
              <Button type="button" variant="primary" onClick={onCreateTeam} disabled={createTeam.isPending}>
                {createTeam.isPending ? toSafeReactNode(t("team.creating")) : toSafeReactNode(t("team.createTeam"))}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {team && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(team.name)}</CardTitle>
              <CardDescription>
                {toSafeReactNode(t("team.role"))}: {toSafeReactNode(team.currentUserRole ?? "member")} · {toSafeReactNode(t("team.membersCount"))}: {team.members.length}
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(t("team.inviteTitle"))}</CardTitle>
              <CardDescription>{toSafeReactNode(t("team.inviteDescription"))}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Input
                  type="email"
                  placeholder={toDisplayString(t("team.memberEmailPlaceholder"))}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="max-w-xs"
                  disabled={!canManageMembers}
                />
                <Button
                  type="button"
                  variant="primary"
                  onClick={onInvite}
                  disabled={!canManageMembers || inviteMember.isPending}
                >
                  {inviteMember.isPending ? toSafeReactNode(t("team.sending")) : toSafeReactNode(t("team.invite"))}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(t("team.membersTitle"))}</CardTitle>
              <CardDescription>{toSafeReactNode(t("team.membersDescription"))}</CardDescription>
            </CardHeader>
            <CardContent>
              {team.members.length === 0 ? (
                <p className="text-sm text-text-muted">{toSafeReactNode(t("team.noMembers"))}</p>
              ) : (
                <ul className="space-y-2">
                  {team.members.map((member) => {
                    const isOwner = member.userId === team.ownerId;
                    return (
                      <li
                        key={toDisplayString(member.id)}
                        className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-2 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm text-text">
                            {toSafeReactNode(member.email ?? member.userId)}
                          </p>
                          <p className="text-xs text-text-muted">
                            {isOwner ? toSafeReactNode(t("team.owner")) : member.role === "admin" ? toSafeReactNode(t("team.admin")) : toSafeReactNode(t("team.member"))}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={!canManageMembers || isOwner || removingUserId === member.userId}
                          onClick={() => onRemove(member.userId)}
                        >
                          {removingUserId === member.userId ? toSafeReactNode(t("team.removing")) : toSafeReactNode(t("team.remove"))}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(t("team.invitesTitle"))}</CardTitle>
              <CardDescription>{toSafeReactNode(t("team.invitesDescription"))}</CardDescription>
            </CardHeader>
            <CardContent>
              {team.invites.length === 0 ? (
                <p className="text-sm text-text-muted">{toSafeReactNode(t("team.noInvites"))}</p>
              ) : (
                <ul className="space-y-2">
                  {team.invites.map((invite) => (
                    <li
                      key={toDisplayString(invite.id)}
                      className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
                    >
                      <span className="truncate">{toSafeReactNode(invite.email)}</span>
                      <span className={invite.accepted ? "text-success" : "text-text-muted"}>
                        {invite.accepted ? toSafeReactNode(t("team.accepted")) : toSafeReactNode(t("team.pending"))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export const teamRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "team",
  component: TeamPage,
});
